/**
 * Workspace Database
 *
 * IndexedDB storage for workspace entries using Dexie.
 * Caches workspace details so the nav panel and settings
 * can show data instantly from cache while fetching fresh data
 * from the server (stale-while-revalidate).
 *
 * Schema:
 * - userWorkspaces table: Stores Workspace records for workspaces the user owns or is a member of.
 *   Only populated by queryUserWorkspaces(). Gallery/discovery results are NOT cached here.
 * - Primary key: id
 * - Indexes: ownerId, *tags (multi-entry for tag-based filtering)
 */

import Dexie, { type Table } from 'dexie';
import type { Workspace } from '@bloomstack/apis/workspace';

export class WorkspaceDatabase extends Dexie {
  userWorkspaces!: Table<Workspace, string>;

  constructor() {
    super('AIMysticWorkspaces');

    // Version 1: old "workspaces" table (polluted with discovery data)
    this.version(1).stores({
      workspaces: 'id, ownerId, *tags',
    });

    // Version 2: rename to "userWorkspaces", drop old table
    this.version(2).stores({
      workspaces: null,                          // Drop the old table
      userWorkspaces: 'id, ownerId, *tags',      // New table (user-scoped only)
    });
  }
}

// Export singleton instance
export const workspaceDb = new WorkspaceDatabase();

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Upsert a single workspace.
 */
export async function putWorkspace(workspace: Workspace): Promise<void> {
  await workspaceDb.userWorkspaces.put(workspace);
}

/**
 * Upsert multiple workspaces (for query results).
 */
export async function bulkPutWorkspaces(workspaces: Workspace[]): Promise<void> {
  await workspaceDb.userWorkspaces.bulkPut(workspaces);
}

/**
 * Get a single workspace by ID.
 */
export async function getWorkspace(id: string): Promise<Workspace | undefined> {
  return workspaceDb.userWorkspaces.get(id);
}

/**
 * Get all cached workspaces.
 */
export async function getAllWorkspaces(): Promise<Workspace[]> {
  return workspaceDb.userWorkspaces.toArray();
}

/**
 * Remove a workspace by ID.
 */
export async function removeWorkspace(id: string): Promise<void> {
  await workspaceDb.userWorkspaces.delete(id);
}
